import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Lightning, Timer, CurrencyDollar } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AutoDispatchCard — one toggle. When on, the POS quotes every enabled
 * third-party brand in parallel the moment an order is ready and no
 * in-house driver is assigned, then dispatches to the FASTEST (tiebreak
 * CHEAPEST) brand.  No per-order picker, no priority list.
 */
export const AutoDispatchCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/store-settings`);
        if (ignore) return;
        setEnabled(!!data.auto_dispatch_third_party);
      } catch (e) {
        console.error(e);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const handleToggle = async (v) => {
    setEnabled(v);
    setSaving(true);
    try {
      await axios.put(`${API}/api/store-settings`, { auto_dispatch_third_party: v });
      toast.success(v ? "Auto-dispatch enabled" : "Auto-dispatch disabled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
      setEnabled(!v);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="auto-dispatch-card" className="border-primary/40">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Lightning size={20} weight="fill" className="text-primary" />
          <CardTitle className="font-heading">Automatic Third-Party Dispatch</CardTitle>
        </div>
        <CardDescription>
          When a delivery order is ready and no in-house driver is assigned, the POS quotes every enabled brand in parallel and dispatches to whichever one gets the food there fastest.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border">
          <div>
            <p className="text-sm font-medium">Enable auto-dispatch</p>
            <p className="text-xs text-muted-foreground">Off by default — turn on once at least one brand has valid credentials.</p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={loading || saving}
            data-testid="auto-dispatch-switch"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-3 rounded-lg border bg-background flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-300 flex items-center justify-center shrink-0">
              <Timer size={16} weight="fill" />
            </div>
            <div>
              <p className="text-sm font-medium">Fastest wins</p>
              <p className="text-xs text-muted-foreground">Compares each brand's estimated dropoff time and picks the earliest one.</p>
            </div>
          </div>
          <div className="p-3 rounded-lg border bg-background flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-300 flex items-center justify-center shrink-0">
              <CurrencyDollar size={16} weight="fill" />
            </div>
            <div>
              <p className="text-sm font-medium">Cheapest breaks the tie</p>
              <p className="text-xs text-muted-foreground">If two brands report the same ETA, the lower delivery fee wins.</p>
            </div>
          </div>
        </div>

        <div className="text-xs text-muted-foreground">
          Every dispatch stores the competing quotes on the order so you can see exactly why one brand beat the others.
        </div>
      </CardContent>
    </Card>
  );
};

export default AutoDispatchCard;
